import axios from 'axios';
import _ from 'lodash';
import EventBus from './event-bus';

axios.defaults.baseURL = process.env.VUE_APP_API_URL;

export default class DataSvc {
  /* Auth */
  static signIn(credentials) {
    return axios.post('users/auth_token', credentials)
      .then((authResponse) => {
        const authToken = authResponse.data.auth_token;

        // set bearer auth token
        axios.defaults.headers.common.Authorization = `Bearer ${authToken}`;

        // grab user corresponding to auth token
        return axios.get('users/me')
          .then((userResponse) => {
            const user = userResponse.data;
            let ret;

            // if user is neither organizer nor admin
            if (!user.permissions.includes('organizers')
              && !user.permissions.includes('superadmin')) {
              ret = Promise.reject(new Error('Invalid email/password combination.'));
              EventBus.$emit('show-warning', {
                msg: '&bull; Invalid email/password combination.',
                title: 'The following errors occured',
              });
            } else {
              user.auth_token = authToken;
              ret = user;
            }

            return ret;
          });
      });
  }

  static signOut(token) {
    return axios.delete(`users/auth_token/${token}`);
  }

  static tokenAuth() {
    return axios.get('tests/auth').then(response => response.data);
  }
  /* / Auth */

  /* Organizations */
  static getOrganizations(params) {
    return axios.get(
      'organizations',
      { params },
    ).then(response => response.data);
  }

  static getOrganization(organizationID) {
    return axios.get(`organizations/${organizationID}`)
      .then(response => response.data);
  }

  static createOrganization(organization) {
    return axios.post(
      'organizations',
      organization,
    ).then(response => response.data);
  }

  static updateOrganization(organization) {
    return axios.put(
      `organizations/${organization.id}`,
      organization,
    ).then(response => response.data);
  }

  static deleteOrganization(organizationID) {
    return axios.delete(
      `organizations/${organizationID}`,
    ).then(response => response.data);
  }

  static getTimezones() {
    return axios.get(
      'organizations/timezones',
    ).then(response => response.data);
  }

  static getCreditCardTransactions(organizationID) {
    const balancePromise = axios.get(
      `organizations/${organizationID}/account`,
    ).then(response => response.data);

    const logPromise = axios.get(
      `organizations/${organizationID}/account/logs`,
    ).then(response => response.data);

    return Promise.all([
      balancePromise,
      logPromise,
    ]).then((data) => {
      const ret = {};
      [ret.balance, ret.log] = data;
      return ret;
    });
  }

  static downloadPayoutReport(organizationID) {
    const params = {
      full_history: true,
      format: 'csv',
    };

    return axios.get(
      `organizations/${organizationID}/account/logs`,
      { params },
    ).then(response => response.data);
  }
  /* / Organizations */

  /* Seasons */
  static getSeasons(organizationID, search) {
    const params = _.extend(
      {},
      search,
      { organization_id: organizationID },
    );

    return axios.get(
      'seasons',
      { params },
    ).then(response => response.data);
  }

  static getSeason(seasonID) {
    return axios.get(`seasons/${seasonID}`)
      .then(response => response.data);
  }

  static createSeason(organizationID, season) {
    const data = _.extend(
      {},
      season,
      { organization_id: organizationID },
    );

    return axios.post(
      'seasons',
      data,
    ).then(response => response.data);
  }

  static updateSeason(season) {
    const data = _.cloneDeep(season);

    if (!data.start_date_time) {
      data.start_date_time = null;
    }

    if (!data.end_date_time) {
      data.end_date_time = null;
    }

    return axios.put(
      `seasons/${season.id}`,
      data,
    ).then(response => response.data);
  }

  static deleteSeason(seasonID) {
    return axios.delete(
      `seasons/${seasonID}`,
    ).then(response => response.data);
  }
  /* / Seasons */

  /* Events */
  static getEvents(organizationID, seasonID, search) {
    const params = _.extend(
      {},
      search,
      {
        organization_id: organizationID,
        season_id: seasonID,
      },
    );

    return axios.get(
      'events',
      { params },
    ).then(response => response.data);
  }

  static getEvent(eventID) {
    return axios.get(`events/${eventID}`)
      .then(response => response.data);
  }

  static createEvent(organizationID, event) {
    const data = _.extend(
      {},
      event,
      { organization_id: organizationID },
    );

    return axios.post(
      'events',
      data,
    ).then(response => response.data);
  }

  static copyEvent(event) {
    return axios.post(
      `events/${event.id}/clone`,
      event,
    ).then(response => response.data);
  }

  static updateEvent(event) {
    return axios.put(
      `events/${event.id}`,
      event,
    ).then(response => response.data);
  }

  static deleteEvent(eventID) {
    return axios.delete(
      `events/${eventID}`,
    ).then(response => response.data);
  }
  /* / Events */

  /* Forms */
  static getForm(formID) {
    return axios.get(
      `forms/${formID}`,
    ).then(response => response.data);
  }

  static getForms(params) {
    return axios.get(
      'forms',
      { params },
    ).then(response => response.data);
  }

  static reorderFormFields(formID, fieldOrder) {
    return axios.put(
      `forms/${formID}/field-order`,
      fieldOrder,
    ).then(response => response.data);
  }

  static copyExistingForm(formID, sourceFormID) {
    const data = {
      source_form_id: sourceFormID,
    };

    return axios.post(
      `forms/${formID}/form-fields`,
      data,
    ).then(response => response.data);
  }

  static attachExistingFormField(formID, formField) {
    const data = {
      form_field_id: formField.id,
      order: formField.order,
      required: formField.required,
      waiver: formField.waiver,
      hidden: formField.hidden,
    };

    return axios.post(
      `forms/${formID}/form-fields`,
      data,
    ).then(response => response.data);
  }

  static removeFormField(formID, formFieldID) {
    return axios.delete(
      `forms/${formID}/form-fields/${formFieldID}`,
    ).then(response => response.data);
  }

  static updateFormsFormField(formID, formField) {
    const data = _.cloneDeep(formField);

    return axios.put(
      `forms/${formID}/form-fields/${formField.id}`,
      data,
    ).then(response => response.data);
  }

  static getPriceModifiers(formID) {
    return axios.get(
      `forms/${formID}/registration-modifier-triggers`,
    ).then(response => response.data);
  }

  static createPriceModifier(formID, priceModifier) {
    const data = {
      form_field_id: priceModifier.form_field_id,
      description: priceModifier.description,
      form_field_value: priceModifier.form_field_value,
      price_modification_flat: priceModifier.price_modification_flat,
      form_field_comparator: '=',
    };

    return axios.post(
      `forms/${formID}/registration-modifier-triggers`,
      data,
    ).then(response => response.data);
  }

  static updatePriceModifier(formID, priceModifier) {
    const data = {
      description: priceModifier.description,
      price_modification_flat: priceModifier.price_modification_flat,
    };

    return axios.put(
      `forms/${formID}/registration-modifier-triggers/${priceModifier.id}`,
      data,
    ).then(response => response.data);
  }

  static deletePriceModifier(formID, priceModifierID) {
    return axios.delete(
      `forms/${formID}/registration-modifier-triggers/${priceModifierID}`,
    ).then(response => response.data);
  }
  /* / Forms */

  /* Form Fields */
  static getFormFields(params) {
    return axios.get(
      'form-fields',
      { params },
    ).then(response => response.data);
  }

  static createEventFormField(formID, formField) {
    const data = _.extend(formField, { form_id: formID });

    if (data.options) {
      data.options = _.split(data.options, ',');
    }

    data.waiver = !!data.waiver;

    return axios.post(
      'form-fields',
      data,
    ).then(response => response.data);
  }

  static deleteEventFormField(fieldID) {
    return axios.delete(
      `form-fields/${fieldID}`,
    ).then(response => response.data);
  }

  static getEventFormFieldTypes() {
    return axios.get(
      'form-fields/types',
    ).then(response => response.data);
  }

  static getPredefinedEventFormFields() {
    return axios.get(
      'form-fields/predefined',
    ).then(response => response.data);
  }
  /* / Form Fields */

  /* Registrants / Registrations */
  static getEventRegistrants(eventID) {
    const params = { event_id: eventID };

    return axios.get(
      'registrants',
      { params },
    ).then(response => response.data);
  }

  static getEventRegistrations(eventID) {
    const params = {
      event_id: eventID,
    };

    return axios.get(
      'registrations',
      { params },
    ).then(response => response.data);
  }

  static getRegistrant(registrantID) {
    return axios.get(
      `registrants/${registrantID}`,
    ).then(response => response.data);
  }

  static createRegistrant(registrant) {
    return axios.post(
      'registrants',
      registrant,
    ).then(response => response.data);
  }

  static updateRegistrant(registrant) {
    return axios.put(
      `registrants/${registrant.id}`,
      registrant,
    ).then(response => response.data);
  }

  static deleteRegistrant(registrantID) {
    return axios.delete(
      `registrants/${registrantID}`,
    ).then(response => response.data);
  }

  static searchRegistrants(organizationID, search) {
    const params = _.extend(
      {},
      search,
      { organization_id: organizationID },
    );

    return axios.get(
      'registrants',
      { params },
    ).then(response => response.data);
  }

  static resendConfirmationEmail(registrationSessionID) {
    return axios.post(
      `registration-sessions/${registrationSessionID}/confirmation-email`,
    ).then(response => response.data);
  }

  static getRegistrationSession(registrationSessionID, detailed) {
    const params = {
      detailed: !!detailed,
    };

    return axios.get(
      `registration-sessions/${registrationSessionID}`,
      { params },
    ).then(response => response.data);
  }

  static getOrderDetails(registrationSessionID) {
    const params = {
      detailed: true,
    };

    return axios.get(
      `registration-sessions/${registrationSessionID}`,
      { params },
    ).then(({ data }) => {
      const eventProps = [
        'id',
        'name',
        'display_start_date_time',
        'display_end_date_time',
        'org_id',
        'org_name',
      ];

      const orderDetails = _.omit(data, ['registrations']);

      // map registration transactions
      const registrationTransactions = [];
      (data.registrations || []).forEach((r) => {
        r.registration_transactions.forEach((t) => {
          const transaction = _.extend(t, {
            id: r.id,
            transaction_id: t.id,
            full_name: r.registrant.full_name,
            amount_refundable: r.amount_refundable,
            total_collected: r.total_collected,
            event: _.pick(t.registration_snapshot.event, eventProps),
            pricing_package_id: t.registration_snapshot.pricing_package.id,
            pricing_package_label: t.registration_snapshot.pricing_package.label,
            promo_code: r.promo_code,
            cancelled: r.cancelled,
            waitlist: r.waitlist,
          });

          registrationTransactions.push(transaction);
        });
      });

      orderDetails.registrationTransactions = _.orderBy(
        registrationTransactions,
        ['full_name', 'timestamp_created_at'],
      );

      // map payment transactions
      const paymentTransactions = [];

      (data.registrations || []).forEach((r) => {
        r.payment_transactions.forEach((t) => {
          const transaction = _.extend(t, {
            id: r.id,
            full_name: r.registrant.full_name,
            event: _.pick(t.registration_snapshot.event, eventProps),
            pricing_package_id: t.registration_snapshot.pricing_package.id,
            pricing_package_label: t.registration_snapshot.pricing_package.label,
          });

          paymentTransactions.push(transaction);
        });
      });

      orderDetails.paymentTransactions = _.orderBy(
        paymentTransactions,
        'timestamp_created_at',
      );

      return orderDetails;
    });
  }

  static getRegistrations(params) {
    return axios.get(
      'registrations',
      { params },
    ).then(response => response.data);
  }

  static getRegistration(registrationID) {
    return axios.get(
      `registrations/${registrationID}`,
    ).then(response => response.data);
  }

  static createRegistration(registration) {
    return axios.post(
      'registrations',
      registration,
    ).then(response => response.data);
  }

  static updateRegistration(registration) {
    const data = {
      form_data: registration.form_data,
    };

    return axios.put(
      `registrations/${registration.id}`,
      data,
    ).then(response => response.data);
  }

  static moveRegistrationToActiveRoster(registration) {
    const data = {
      id: registration.id,
      registration_session_uuid: registration.registration_session_uuid,
      roster_id: registration.roster_id,
      registrant_id: registration.registrant_id,
      waitlist: false,
    };

    return axios.put(
      `registrations/${registration.id}`,
      data,
    ).then(response => response.data);
  }

  static deleteRegistration(registrationID) {
    return axios.delete(
      `registrations/${registrationID}`,
    ).then(response => response.data);
  }

  static cancelRegistration(registrationID) {
    const data = {
      cancelled: true,
    };

    return axios.put(
      `registrations/${registrationID}`,
      data,
    ).then(response => response.data);
  }

  static moveRegistration(registrationID, move) {
    const data = {
      event_id: move.eventID,
      pricing_package_id: move.pricingPackageID,
    };

    return axios.put(
      `registrations/${registrationID}/event`,
      data,
    ).then(response => response.data);
  }

  static recordPayment(registrationID, payment) {
    const data = _.clone(payment);

    delete data.registrationID;

    return axios.post(
      `registrations/${registrationID}/payment`,
      data,
    ).then(response => response.data);
  }

  static recordRefund(registrationID, refund) {
    const data = _.clone(refund);

    data.override_amount_refundable = true;

    delete data.registrationID;

    return axios.post(
      `registrations/${registrationID}/refunds`,
      data,
    ).then(response => response.data);
  }

  static adjustRegistration(registrationID, adustment) {
    return axios.post(
      `registrations/${registrationID}/adjustment`,
      adustment,
    ).then(response => response.data);
  }

  static issueRegistrationRefund(registrationID, refund) {
    const data = {
      amount: Math.abs(refund.amount),
      refund_type: 'credit_card_refund',
      override_amount_refundable: true,
    };

    return axios.post(
      `registrations/${registrationID}/refunds`,
      data,
    ).then(response => response.data);
  }

  static getRegistrationSessionFromTransaction(organizationID, sourcePaymentID) {
    const params = {
      organization_id: organizationID,
      source_payment_id: sourcePaymentID,
    };

    return axios.get(
      'registration-sessions',
      { params },
    ).then(response => response.data[0]);
  }
  /* / Registrants / Registrations */

  /* Pricing */
  static getEventPricing(eventID) {
    const params = {
      event_id: eventID,
    };

    return axios.get(
      'pricing-packages',
      { params },
    ).then(response => response.data);
  }

  static getPricing(pricingID) {
    return axios.get(
      `pricing-packages/${pricingID}`,
    ).then(response => response.data);
  }

  static createPricing(eventID, pricing) {
    const data = _.extend(
      {},
      pricing,
      { event_id: eventID },
    );

    return axios.post(
      'pricing-packages',
      data,
    ).then(response => response.data);
  }

  static updatePricing(pricing) {
    const data = _.clone(pricing);

    if (!data.min_date_time) {
      data.min_date_time = null;
    }

    if (!data.max_date_time) {
      data.max_date_time = null;
    }

    return axios.put(
      `pricing-packages/${pricing.id}`,
      data,
    ).then(response => response.data);
  }

  static deletePricing(pricingID) {
    return axios.delete(
      `pricing-packages/${pricingID}`,
    ).then(response => response.data);
  }
  /* / Pricing */

  /* Rosters */
  static getRosters(params) {
    return axios.get(
      'rosters',
      { params },
    ).then(response => response.data);
  }

  static getRoster(rosterID) {
    return axios.get(
      `rosters/${rosterID}`,
    ).then(response => response.data);
  }

  static updateRoster(roster) {
    return axios.put(
      `rosters/${roster.id}`,
      roster,
    ).then(response => response.data);
  }
  /* / Rosters */

  /* Reports */
  static getFields(organizationID, reportType) {
    const params = {
      organization_id: organizationID,
      report_type: reportType,
    };

    return axios.get(
      'reports/fields',
      { params },
    ).then(response => response.data);
  }

  static getComparators() {
    return axios.get(
      'reports/comparators',
    ).then(response => response.data);
  }

  static generateReport(organizationID, fields, filters, additional, csv) {
    const data = _.extend({
      organization_id: organizationID,
      fields: _.cloneDeep(fields),
      filters: _.cloneDeep(filters),
    }, additional);

    const config = {};

    if (csv) {
      data.format = 'csv';
      config.responseType = 'blob';
    }

    return axios.post(
      'reports',
      data,
      config,
    ).then(response => response.data);
  }
  /* / Reports */

  /* Communications */
  static getCommunications(params) {
    return axios.get(
      'email/logs',
      { params },
    ).then(response => response.data);
  }

  static getCommunicationByID(communicationID, type) {
    const params = {
      type: type,
    };

    return axios.get(
      'email/logs/' + communicationID,
      { params },
    ).then(response => response.data);
  }

  static getCommunication(communicationID, organizationID) {
    return this.getCommunications(organizationID)
      .then((comms) => {
        const c = _.find(comms, { id: parseInt(communicationID) });

        if (!c) {
          EventBus.$emit('show-warning', {
            msg: '&bull; Message not found.',
            title: 'The following errors occured',
          });
        }

        return c || Promise.reject(new Error('Message not found.'));
      });

    /*
    return axios.get(
      `email/messages/${communicationID}`,
    ).then(response => response.data);
    */
  }

  static sendCommunication(message) {
    return axios.post(
      'email/messages',
      message,
    ).then(response => response.data);
  }
  /* / Communications */

  /* Files */
  static getFiles(params) {
    return axios.get(
      'files',
      { params },
    ).then(response => response.data);
  }

  static uploadFile(data) {
    return axios.post(
      'files',
      data,
    ).then(response => response.data);
  }

  static getRegistrationFiles(registrationID) {
    const params = {
      registration_id: registrationID,
    };

    return axios.get(
      'registration-files',
      { params },
    ).then(response => response.data);
  }

  static deleteRegistrationFile(uuid) {
    return axios.delete(
      `registration-files/${uuid}`,
    ).then(response => response.data);
  }

  static uploadRegistrationFile(data) {
    return axios.post(
      'registration-files',
      data,
    ).then(response => response.data);
  }

  static downloadFile(uuid) {
    return axios.get(
      `registration-files/${uuid}/data`,
      { responseType: 'blob' },
    ).then(response => response.data);
  }

  static downloadFileArchive(data) {
    return axios.post(
      'registration-files/archive',
      data,
    ).then(response => response.data);
  }
  /* / Files */

  /* Payments */
  static getPaymentTypes() {
    return axios.get(
      'payments/types',
    ).then(response => response.data);
  }

  static getRefundTypes() {
    return axios.get(
      'payments/refund-types',
    ).then(response => response.data);
  }

  static getTransactionTypes() {
    return axios.get(
      'reports/payment-transaction-types',
    ).then(response => response.data);
  }
  /* / Payments */

  /* Promo Code */
  static getEventPromoCodes(eventID) {
    const params = {
      event_id: eventID,
    };

    return axios.get(
      'promo-codes',
      { params },
    ).then(response => response.data);
  }

  static getPromoCode(promoCodeID) {
    return axios.get(
      `promo-codes/${promoCodeID}`,
    ).then(response => response.data);
  }

  static createPromoCode(eventID, promoCode) {
    const data = _.extend(
      {},
      promoCode,
      { event_id: eventID },
    );

    return axios.post(
      'promo-codes',
      data,
    ).then(response => response.data);
  }

  static updatePromoCode(promoCode) {
    return axios.put(
      `promo-codes/${promoCode.id}`,
      promoCode,
    ).then(response => response.data);
  }

  static deletePromoCode(promoCodeID) {
    return axios.delete(
      `promo-codes/${promoCodeID}`,
    ).then(response => response.data);
  }
  /* / Promo Code */

  /* Users */
  static getUsers() {
    return axios.get(
      'users',
    ).then(response => response.data);
  }

  static getUser(userID) {
    return axios.get(
      `users/${userID}`,
    ).then(response => response.data);
  }

  static createUser(user) {
    const data = _.extend(
      {},
      user,
    );

    return axios.post(
      'users',
      data,
    ).then(response => response.data);
  }

  static updateUser(user) {
    const data = _.extend(
      {},
      user,
    );

    if (!data.password) {
      delete data.password;
    }

    return axios.put(
      `users/${user.id}`,
      data,
    ).then(response => response.data);
  }

  static deleteUser(userID) {
    return axios.delete(
      `users/${userID}`,
    ).then(response => response.data);
  }

  static updatePermissions(userID, permissions) {
    return axios.put(
      `users/${userID}/permissions`,
      permissions,
    ).then(response => response.data);
  }
  /* / Users */

  /* Helpers / Utility */
  static configureErrorHandling(errorHandler) {
    // handle API errors
    axios.interceptors.response.use(
      response => response,
      (error) => {
        const { response } = error;
        errorHandler(response);
        return Promise.reject(response);
      },
    );
  }
  /* / Helpers / Utility */
}
